/**
 * Validation Engine
 * Core validation logic for forms and fields with support for cross-field validation
 *
 * Features:
 * - Single and multi-field validation
 * - Complete ValidationContext with all form data
 * - Cross-field validation (e.g., password confirmation)
 * - Dependent field validation
 * - Custom validators with full context access
 * - Performance optimized with memoization
 */

import type {
  FormSchema,
  FormControl,
  ValidationContext,
  ValidationError,
  FieldValidationResult,
  FormValidationResult,
  ValidatorFn,
  ValidationRule,
} from '@atlas-forms/types-js';
import { BUILT_IN_VALIDATORS } from './validators';

/**
 * Validator memoization cache
 * Stores results to avoid redundant validation calls
 */
interface MemoizedValidation {
  value: unknown;
  hash: string;
  result: string | null;
}

/**
 * Validation engine for validating forms and fields with complete context support
 *
 * Handles:
 * - Required field validation
 * - String length constraints (minLength, maxLength)
 * - Numeric constraints (min, max)
 * - Pattern matching (regex)
 * - Cross-field validation (password confirmation, date ranges, etc.)
 * - Dependent field validation
 * - Custom validators with ValidationContext
 * - Performance optimization via memoization
 */
export class ValidationEngine {
  private customValidators: Map<string, ValidatorFn> = new Map();
  private memoCache: Map<string, MemoizedValidation> = new Map();
  private fieldRules: Map<string, ValidationRule[]> = new Map();

  constructor() {
    // Register built-in validators
    Object.entries(BUILT_IN_VALIDATORS).forEach(([name, validator]) => {
      this.customValidators.set(name, validator);
    });
  }

  /**
   * Register custom validator function
   * Validates validator function before registration to ensure type safety.
   *
   * @param name Unique validator identifier
   * @param validator Validator function with full context support
   * @throws Error if name is invalid or validator is not a function
   */
  registerValidator(name: string, validator: ValidatorFn): void {
    // SECURITY: Validate input parameters
    if (!name || typeof name !== 'string') {
      throw new Error('Validator name must be a non-empty string');
    }

    if (typeof validator !== 'function') {
      throw new Error('Validator must be a function');
    }

    // SECURITY: Check function has correct signature (should accept 3 parameters)
    // This is a runtime check to prevent misuse
    const fnLength = validator.length;
    if (fnLength !== 3) {
      console.warn(
        `Warning: Validator function "${name}" has ${fnLength} parameters but should have 3 (value, control, context)`
      );
      // Still allow registration but warn developer
    }

    // SECURITY: Prevent overriding built-in validators unless explicitly needed
    // This helps avoid accidental shadowing of core validation logic
    if (Object.prototype.hasOwnProperty.call(BUILT_IN_VALIDATORS, name)) {
      console.warn(
        `Warning: Registering validator "${name}" overrides a built-in validator. This may cause unexpected behavior.`
      );
    }

    this.customValidators.set(name, validator);
    // Clear cache when registering new validators
    this.memoCache.clear();
  }

  /**
   * Get validator by name
   * @param name Validator identifier
   * @returns Validator function or undefined if not found
   */
  getValidator(name: string): ValidatorFn | undefined {
    return this.customValidators.get(name);
  }

  /**
   * Clear validation cache
   * Useful when form data changes significantly
   */
  clearCache(): void {
    this.memoCache.clear();
  }

  addRule(fieldId: string, rule: ValidationRule): void {
    const rules = this.fieldRules.get(fieldId) ?? [];
    rules.push(rule);
    this.fieldRules.set(fieldId, rules);
    this.memoCache.clear();
  }

  removeRule(fieldId: string, ruleType: string): void {
    const rules = this.fieldRules.get(fieldId);
    if (rules) {
      this.fieldRules.set(fieldId, rules.filter((r) => r.type !== ruleType));
      this.memoCache.clear();
    }
  }

  /**
   * Validate single field with complete validation context
   *
   * Executes all configured validators for the field and returns the first error found.
   * The ValidationContext is fully constructed and passed to all validators.
   * Includes contextual error messages with recovery suggestions.
   *
   * @param control The FormControl being validated
   * @param value The value to validate
   * @param context Optional partial context (schema, formValues, etc.)
   * @returns ValidationError if invalid, null if valid
   *
   * @example
   * ```typescript
   * const engine = new ValidationEngine();
   * const error = engine.validateField(
   *   { id: 'password_confirm', required: true },
   *   'pass123',
   *   { schema: mySchema, formValues: { password: 'pass123' } }
   * );
   * if (error) {
   *   console.log(`${error.message} (${error.code})`);
   * }
   * ```
   */
  validateField(
    control: FormControl,
    value: unknown,
    context?: Partial<ValidationContext>
  ): ValidationError | null {
    const error = this.runFieldRules(control, value, context);
    // The designer's "Error Message" (validation.message) replaces the default message for any of
    // the field's failures, including "required", keeping the error's code, rule and metadata.
    const customMessage = control.validation?.message;
    if (error && customMessage && customMessage.trim() !== '') {
      return { ...error, message: customMessage };
    }
    return error;
  }

  /**
   * Runs the built-in rules in order and returns the first failure with its default message.
   */
  private runFieldRules(
    control: FormControl,
    value: unknown,
    context?: Partial<ValidationContext>
  ): ValidationError | null {
    // Build complete validation context
    const fullContext: ValidationContext = {
      schema: context?.schema || { version: '1.0', metadata: { formID: 'unknown', title: 'Unknown' }, controls: [] },
      formValues: context?.formValues || {},
      control: context?.control || control,
      customContext: context?.customContext,
    };

    // Check required field
    if (control.required) {
      const error = BUILT_IN_VALIDATORS.required(value, control, fullContext);
      if (error) {
        return {
          field: control.id,
          message: error,
          code: 'REQUIRED',
          rule: 'required',
          value,
          metadata: {
            fieldId: control.id,
            fieldLabel: control.label,
            suggestion: `${control.label || 'This field'} is required. Please provide a value.`,
          },
        };
      }
    }

    // Skip other validations if value is empty and field is not required
    if (
      (value === null || value === undefined || value === '') &&
      !control.required
    ) {
      return null;
    }

    // Check minLength constraint
    if (control.validation?.minLength) {
      const error = BUILT_IN_VALIDATORS.minLength(value, control, fullContext);
      if (error) {
        const actualLen = typeof value === 'string' ? value.length : 0;
        return {
          field: control.id,
          message: error,
          code: 'MIN_LENGTH',
          rule: 'minLength',
          value,
          metadata: {
            fieldId: control.id,
            fieldLabel: control.label,
            minLength: control.validation.minLength,
            actualLength: actualLen,
            suggestion: `${control.label || 'This field'} must be at least ${control.validation.minLength} characters (currently ${actualLen}).`,
          },
        };
      }
    }

    // Check maxLength constraint
    if (control.validation?.maxLength) {
      const error = BUILT_IN_VALIDATORS.maxLength(value, control, fullContext);
      if (error) {
        const isList = Array.isArray(value);
        const actualLen = typeof value === 'string' || isList ? value.length : 0;
        const unit = isList ? 'options' : 'characters';
        return {
          field: control.id,
          message: error,
          code: 'MAX_LENGTH',
          rule: 'maxLength',
          value,
          metadata: {
            fieldId: control.id,
            fieldLabel: control.label,
            maxLength: control.validation.maxLength,
            actualLength: actualLen,
            suggestion: `${control.label || 'This field'} must be no more than ${control.validation.maxLength} ${unit} (currently ${actualLen}).`,
          },
        };
      }
    }

    // Check date limits set by the designer in config.min / config.max (date and datetime).
    // Both sides are normalised to one text format so they order correctly as strings:
    // date → YYYY-MM-DD, datetime → YYYY-MM-DDTHH:mm. A date-only datetime limit widens to
    // the start (min) or end (max) of that day, so a time-of-day limit on the same day is enforced.
    if (control.type === 'date' || control.type === 'datetime') {
      const isDatetime = control.type === 'datetime';
      const normalise = (raw: unknown, edge: 'start' | 'end'): string => {
        if (typeof raw !== 'string' || !raw.trim()) return '';
        const text = raw.trim();
        if (!isDatetime) return text.slice(0, 10);
        if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return edge === 'start' ? `${text}T00:00` : `${text}T23:59`;
        return text.slice(0, 16);
      };
      const dateValue = normalise(value, 'start');
      const minDate = normalise(control.config?.min, 'start');
      const maxDate = normalise(control.config?.max, 'end');
      const shown = (bound: string) => (isDatetime ? bound.replace('T', ' ') : bound);
      let dateMessage: string | null = null;
      if (dateValue && minDate && dateValue < minDate) {
        dateMessage = `${control.label || control.id} must be on or after ${shown(minDate)}`;
      } else if (dateValue && maxDate && dateValue > maxDate) {
        dateMessage = `${control.label || control.id} must be on or before ${shown(maxDate)}`;
      }
      if (dateMessage) {
        return {
          field: control.id,
          message: dateMessage,
          code: 'DATE_RANGE',
          rule: 'dateLimit',
          value,
          metadata: {
            fieldId: control.id,
            fieldLabel: control.label,
            minDate: minDate || undefined,
            maxDate: maxDate || undefined,
            suggestion: `${control.label || 'This field'} must be between ${minDate ? shown(minDate) : '…'} and ${maxDate ? shown(maxDate) : '…'}.`,
          },
        };
      }
    }

    // URL controls must hold an http or https link (see urlValidator). Empty values are
    // handled by the required rule above.
    if (control.type === 'url') {
      const urlError = BUILT_IN_VALIDATORS.url(value, control, fullContext);
      if (urlError) {
        return {
          field: control.id,
          message: `${control.label || control.id} must be a valid URL`,
          code: 'URL',
          rule: 'url',
          value,
          metadata: {
            fieldId: control.id,
            fieldLabel: control.label,
            suggestion: `${control.label || 'This field'} must be a web link starting with http:// or https://.`,
          },
        };
      }
    }

    // Email controls must hold a valid address (see emailValidator). Empty values are handled by
    // the required rule above. Without this, an email control accepted any text, because the
    // format check only ran when a schema added an explicit `email` validation rule. A schema
    // that sets its own pattern keeps that rule as the check, so its error is still reported as
    // a pattern error.
    if (control.type === 'email' && !control.validation?.pattern) {
      const emailError = BUILT_IN_VALIDATORS.email(value, control, fullContext);
      if (emailError) {
        return {
          field: control.id,
          message: `${control.label || control.id} must be a valid email address`,
          code: 'EMAIL',
          rule: 'email',
          value,
          metadata: {
            fieldId: control.id,
            fieldLabel: control.label,
            suggestion: `${control.label || 'This field'} must look like name@example.com.`,
          },
        };
      }
    }

    // Check min numeric constraint
    if (control.validation?.min !== undefined) {
      const error = BUILT_IN_VALIDATORS.min(value, control, fullContext);
      if (error) {
        return {
          field: control.id,
          message: error,
          code: 'MIN_VALUE',
          rule: 'min',
          value,
          metadata: {
            fieldId: control.id,
            fieldLabel: control.label,
            minValue: control.validation.min,
            actualValue: value,
            suggestion: `${control.label || 'This field'} must be at least ${control.validation.min} (entered: ${value}).`,
          },
        };
      }
    }

    // Check max numeric constraint
    if (control.validation?.max !== undefined) {
      const error = BUILT_IN_VALIDATORS.max(value, control, fullContext);
      if (error) {
        return {
          field: control.id,
          message: error,
          code: 'MAX_VALUE',
          rule: 'max',
          value,
          metadata: {
            fieldId: control.id,
            fieldLabel: control.label,
            maxValue: control.validation.max,
            actualValue: value,
            suggestion: `${control.label || 'This field'} must be no more than ${control.validation.max} (entered: ${value}).`,
          },
        };
      }
    }

    // Check pattern constraint
    if (control.validation?.pattern) {
      const error = BUILT_IN_VALIDATORS.pattern(value, control, fullContext);
      if (error) {
        return {
          field: control.id,
          message: error,
          code: 'PATTERN',
          rule: 'pattern',
          value,
          metadata: {
            fieldId: control.id,
            fieldLabel: control.label,
            pattern: control.validation.pattern,
            suggestion: `${control.label || 'This field'} format is invalid. Please check the format and try again.`,
          },
        };
      }
    }

    return null;
  }

  /**
   * Validate entire form with proper dependency ordering
   *
   * Validates all fields in the form, building a complete ValidationContext
   * with all form values available to each field's validators.
   *
   * Supports cross-field validation where validators can access other field values
   * to implement validation rules like:
   * - Password confirmation (field A must equal field B)
   * - Date ranges (end date must be after start date)
   * - Dependent validation (if field A = X, then field B is required)
   *
   * @param schema The FormSchema containing all controls
   * @param values Object with all form field values (fieldId -> value)
   * @returns FormValidationResult with detailed error information and summary
   *
   * @example
   * ```typescript
   * const engine = new ValidationEngine();
   * const result = engine.validateForm(schema, {
   *   password: 'MyPass123',
   *   passwordConfirm: 'MyPass123',
   *   email: 'user@example.com',
   * });
   *
   * if (!result.isValid) {
   *   console.log(result.errors); // All validation errors
   *   console.log(result.summary); // Error counts and statistics
   * }
   * ```
   */
  validateForm(
    schema: FormSchema,
    values: Record<string, unknown>
  ): FormValidationResult {
    const errors: ValidationError[] = [];
    const fieldResults: Record<string, FieldValidationResult> = {};

    // Build complete validation context once for the entire form
    const formContext: Partial<ValidationContext> = {
      schema,
      formValues: values,
    };

    // Validate each control with full form context
    schema.controls.forEach((control: any) => {
      const value = values[control.id];

      // Build context specific to this field
      const fieldContext: Partial<ValidationContext> = {
        ...formContext,
        control,
      };

      // Validate the field
      const error = this.validateField(control, value, fieldContext);

      // Record the result
      const result: FieldValidationResult = {
        fieldId: control.id,
        isValid: !error,
      };

      if (error) {
        result.error = error;
        errors.push(error);
      }

      fieldResults[control.id] = result;
    });

    return {
      isValid: errors.length === 0,
      errors,
      fieldResults,
      summary: {
        totalFields: schema.controls.length,
        validFields: schema.controls.length - errors.length,
        invalidFields: errors.length,
        errorCount: errors.length,
      },
    };
  }

  /**
   * Validate a field with cross-field validation
   *
   * For cases where validation depends on values from other fields.
   *
   * @param fieldId The field ID to validate
   * @param schema The form schema
   * @param values All form values
   * @param customValidator Optional custom validator function
   * @returns ValidationError or null
   *
   * @example
   * ```typescript
   * // Validate password confirmation
   * const error = engine.validateFieldWithContext(
   *   'passwordConfirm',
   *   schema,
   *   { password: 'pass123', passwordConfirm: 'pass123' },
   *   (value, control, context) => {
   *     if (value !== context.formValues['password']) {
   *       return 'Passwords do not match';
   *     }
   *     return null;
   *   }
   * );
   * ```
   */
  validateFieldWithContext(
    fieldId: string,
    schema: FormSchema,
    values: Record<string, unknown>,
    customValidator?: ValidatorFn
  ): ValidationError | null {
    const control = schema.controls.find((c: any) => c.id === fieldId);
    if (!control) {
      return {
        field: fieldId,
        message: 'Field not found in schema',
        code: 'FIELD_NOT_FOUND',
        rule: 'schema',
        value: values[fieldId],
      };
    }

    const context: Partial<ValidationContext> = {
      schema,
      formValues: values,
      control,
    };

    // If custom validator provided, use it first
    if (customValidator) {
      const error = customValidator(values[fieldId], control, context as ValidationContext);
      if (error) {
        return {
          field: fieldId,
          message: error,
          code: 'CUSTOM_VALIDATION',
          rule: 'custom',
          value: values[fieldId],
        };
      }
    }

    // Fall back to standard validation
    return this.validateField(control, values[fieldId], context);
  }
}

/**
 * Create and return a singleton validation engine
 */
let engineInstance: ValidationEngine | null = null;

/**
 * Get or create singleton engine
 */
export function getValidationEngine(): ValidationEngine {
  if (!engineInstance) {
    engineInstance = new ValidationEngine();
  }
  return engineInstance;
}

/**
 * Reset singleton engine (useful for testing)
 */
export function resetValidationEngine(): void {
  engineInstance = null;
}
